import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import MoneyInput from './shared/MoneyInput';
import { api } from './api';
import { errorMessage } from './errors';

// Mirrors leadgen_router (KINDS, FIELD_TYPES, MAX_FIELDS, SLUG_RE, TEXT_LIMITS).
const KINDS = [
  ['mortgage', 'Mortgage'], ['buyer', 'Buyer'], ['seller', 'Seller'], ['open_house', 'Open house'], ['general', 'General'],
];
const FIELD_TYPES = [['text', 'Text'], ['email', 'Email'], ['tel', 'Phone'], ['textarea', 'Long text']];
const MAX_FIELDS = 20;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,48}$/;
const DEFAULT_FIELDS = [
  { key: 'first_name', label: 'First Name', type: 'text', required: true },
  { key: 'last_name', label: 'Last Name', type: 'text', required: false },
  { key: 'email', label: 'Email', type: 'email', required: true },
  { key: 'phone', label: 'Phone', type: 'tel', required: false },
];

const LABEL = 'text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1';
const keyFromLabel = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[^a-z]+/, '').slice(0, 40);

const blank = () => ({
  slug: '', name: '', kind: 'mortgage', headline: '', subheadline: '', landing_headline: '', landing_body: '',
  fields: DEFAULT_FIELDS.map(f => ({ ...f })), pool: [], cost: null, active: true,
});

const fromForm = (f, staffIds) => ({
  slug: f.slug, name: f.name || '', kind: f.kind || 'mortgage', headline: f.headline || '', subheadline: f.subheadline || '',
  landing_headline: f.landing_headline || '', landing_body: f.landing_body || '',
  fields: (f.fields?.length ? f.fields : DEFAULT_FIELDS).map(x => ({ key: x.key, label: x.label, type: x.type, required: !!x.required })),
  // a user who left the team stays out of the editor and drops off on save
  pool: (f.round_robin_pool || []).filter(id => staffIds.has(id)),
  cost: f.cost_per_lead_cents == null ? null : f.cost_per_lead_cents / 100,
  active: f.active !== false,
});

function Field({ label, htmlFor, children }) {
  return <div><label htmlFor={htmlFor} className={LABEL}>{label}</label>{children}</div>;
}

// Create (form = null) or edit one lead form. `staff` is the /crm/staff roster.
export default function LeadFormEditor({ open, form, staff, onClose, onSaved }) {
  const [v, setV] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setV(form ? fromForm(form, new Set(staff.map(s => s.user_id))) : blank());
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form]);

  const set = (k) => (val) => setV(prev => ({ ...prev, [k]: val }));
  const setField = (i, patch) => setV(prev => ({ ...prev, fields: prev.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) }));
  const togglePool = (id, on) => setV(prev => ({ ...prev, pool: on ? [...prev.pool, id] : prev.pool.filter(x => x !== id) }));

  const problem = () => {
    if (!form && !SLUG_RE.test(v.slug)) return 'Slug uses lowercase letters, numbers and hyphens (2-49 characters)';
    if (!v.name.trim()) return 'Give the form a name';
    if (v.fields.length === 0) return 'Add at least one field';
    const keys = new Set();
    for (const f of v.fields) {
      if (!f.label.trim()) return 'Every field needs a label';
      if (!/^[a-z][a-z0-9_]{0,39}$/.test(f.key)) return `Field key "${f.key}" uses lowercase letters, digits and underscores`;
      if (keys.has(f.key)) return `Two fields share the key "${f.key}"`;
      keys.add(f.key);
    }
    return '';
  };

  const save = async () => {
    const p = problem();
    setError(p);
    if (p) return;
    const body = {
      name: v.name.trim(), kind: v.kind, headline: v.headline, subheadline: v.subheadline,
      landing_headline: v.landing_headline, landing_body: v.landing_body,
      fields: v.fields.map(f => ({ ...f, label: f.label.trim() })), round_robin_pool: v.pool,
      cost_per_lead_cents: v.cost == null ? null : Math.round(v.cost * 100), active: v.active,
    };
    setSaving(true);
    try {
      if (form) await api.patch(`/v2/leadgen/forms/${form.form_id}`, body);
      else await api.post('/v2/leadgen/forms', { ...body, slug: v.slug });
      toast({ title: form ? 'Form saved' : 'Form created' });
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-form-editor">
        <DialogHeader>
          <DialogTitle>{form ? 'Edit lead form' : 'New lead form'}</DialogTitle>
          <DialogDescription>Fields, who gets the lead, and what it costs.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name" htmlFor="lf-name">
              <Input id="lf-name" data-testid="lf-name" value={v.name} maxLength={120} onChange={(e) => set('name')(e.target.value)} />
            </Field>
            <Field label="Slug" htmlFor="lf-slug">
              <Input id="lf-slug" data-testid="lf-slug" value={v.slug} disabled={!!form} placeholder="buyer-signup"
                onChange={(e) => set('slug')(e.target.value)} />
              {!form && <p className="text-[11px] text-slate-400 mt-1">Becomes part of the embed URL. Cannot be changed later.</p>}
            </Field>
            <Field label="Kind">
              <Select value={v.kind} onValueChange={set('kind')}>
                <SelectTrigger data-testid="lf-kind" className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map(([k, n]) => <SelectItem key={k} value={k}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Cost per lead" htmlFor="lf-cost">
              <MoneyInput id="lf-cost" data-testid="lf-cost" cents value={v.cost} onChange={set('cost')} placeholder="$0.00" />
            </Field>
            <Field label="Headline" htmlFor="lf-headline">
              <Input id="lf-headline" data-testid="lf-headline" value={v.headline} maxLength={150} onChange={(e) => set('headline')(e.target.value)} />
            </Field>
            <Field label="Subheadline" htmlFor="lf-subheadline">
              <Input id="lf-subheadline" data-testid="lf-subheadline" value={v.subheadline} maxLength={300} onChange={(e) => set('subheadline')(e.target.value)} />
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className={LABEL + ' mb-0'}>Fields ({v.fields.length}/{MAX_FIELDS})</span>
              <button type="button" data-testid="lf-add-field" disabled={v.fields.length >= MAX_FIELDS}
                onClick={() => setV(prev => ({ ...prev, fields: [...prev.fields, { key: '', label: '', type: 'text', required: false }] }))}
                className="text-xs font-medium text-[#070B5F] flex items-center gap-1 disabled:opacity-40"><Plus size={14} /> Add field</button>
            </div>
            <p className="text-[11px] text-slate-400 mb-2">
              Fields keyed first_name, last_name, email and phone fill the contact record; any other field is saved with the submission.
            </p>
            <ul className="space-y-2">
              {v.fields.map((f, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2" data-testid={`lf-field-${i}`}>
                  <Input aria-label="Field label" data-testid={`lf-field-label-${i}`} className="flex-1 min-w-[8rem]" placeholder="Label" value={f.label} maxLength={80}
                    onChange={(e) => {
                      // a key that still follows the label keeps following it
                      const follows = f.key === keyFromLabel(f.label);
                      setField(i, { label: e.target.value, ...(follows ? { key: keyFromLabel(e.target.value) } : {}) });
                    }} />
                  <Input aria-label="Field key" data-testid={`lf-field-key-${i}`} className="w-32 font-mono text-xs" placeholder="key" value={f.key} maxLength={40}
                    onChange={(e) => setField(i, { key: e.target.value })} />
                  <Select value={f.type} onValueChange={(t) => setField(i, { type: t })}>
                    <SelectTrigger aria-label="Field type" data-testid={`lf-field-type-${i}`} className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELD_TYPES.map(([k, n]) => <SelectItem key={k} value={k}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                  <label className="flex items-center gap-1 text-xs text-slate-600">
                    <Checkbox data-testid={`lf-field-required-${i}`} checked={f.required} onCheckedChange={(c) => setField(i, { required: c === true })} /> Required
                  </label>
                  <button type="button" aria-label="Remove field" data-testid={`lf-field-remove-${i}`} className="p-2 hover:bg-slate-100 rounded-lg"
                    onClick={() => setV(prev => ({ ...prev, fields: prev.fields.filter((_, j) => j !== i) }))}><Trash2 size={14} className="text-slate-500" /></button>
                </li>
              ))}
            </ul>
          </div>

          <Field label="Landing page headline" htmlFor="lf-landing-headline">
            <Input id="lf-landing-headline" data-testid="lf-landing-headline" value={v.landing_headline} maxLength={150} onChange={(e) => set('landing_headline')(e.target.value)} />
          </Field>
          <Field label="Landing page text" htmlFor="lf-landing-body">
            <textarea id="lf-landing-body" data-testid="lf-landing-body" rows={3} maxLength={2000} value={v.landing_body} onChange={(e) => set('landing_body')(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-input bg-transparent text-sm focus:outline-none focus:border-[#070B5F]" />
          </Field>

          <div>
            <span className={LABEL}>Assign leads to (round robin)</span>
            {staff.length === 0 ? <p className="text-xs text-slate-400">No staff to choose from.</p> : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="lf-pool">
                {staff.map(s => (
                  <li key={s.user_id}>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox data-testid={`lf-pool-${s.user_id}`} checked={v.pool.includes(s.user_id)} onCheckedChange={(c) => togglePool(s.user_id, c === true)} />
                      {s.name || s.email}
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-slate-400 mt-1">Nobody selected: the lead is created unassigned.</p>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch data-testid="lf-active" checked={v.active} onCheckedChange={set('active')} /> Live — accepting submissions
          </label>
          {error && <p role="alert" data-testid="lf-error" className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm rounded-lg hover:bg-slate-100">Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="lf-save" className="btn-gold">{saving ? 'Saving…' : form ? 'Save form' : 'Create form'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
